import { Component } from '@theme/component';

/**
 * The review wall: chips filter the cards, a counter says how many are left,
 * and a button opens the ones beyond the limit.
 *
 * Layout is not in here. The columns are CSS (`columns: 2/3` in
 * sections/lift-reviews.liquid), so hiding a card re-flows the rest by itself —
 * no measuring, no resize handler, no re-inserting nodes. Same split as
 * lift-ugc, where the strip is scroll-snap and the class only plays videos.
 *
 * The chips are rendered by Liquid, not built here: they are on the page before
 * this module is fetched, so nothing shifts on load and the section still reads
 * completely without JavaScript — every card visible, chips inert.
 *
 * A ref belongs to the NEAREST enclosing component (assets/component.js:171).
 * Every ref below is ours, and none is required: count, empty box and button
 * only exist when their settings are filled.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [count]
 * @property {HTMLElement} [empty]
 * @property {HTMLElement} [grid]
 * @property {HTMLButtonElement} [more]
 *
 * @extends {Component<Refs>}
 */
class LiftReviews extends Component {
  /** Chip currently pressed. Empty string is "All". @type {string} */
  #tag = '';

  /** @type {boolean} */
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    this.#render();
  }

  /** The theme editor re-renders the section, which replaces every card. */
  updatedCallback() {
    super.updatedCallback();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /** @returns {HTMLElement[]} */
  get #cards() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-lift-review]')]);
  }

  /** @returns {HTMLButtonElement[]} */
  get #chips() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-lift-chip]')]);
  }

  /** How many cards show before the button is pressed. */
  get #limit() {
    const limit = Number.parseInt(this.dataset.liftLimit ?? '', 10);
    return Number.isFinite(limit) && limit > 0 ? limit : Infinity;
  }

  /**
   * One chip filters on size OR occasion — the row mixes both, exactly as the
   * prototype does, so a card matches when either side of it does.
   *
   * Bound on every chip rather than on the row: when Horizon delegates, it
   * replaces `event.target` with the element carrying the `on:` attribute
   * (assets/component.js:245-256), so a row-level handler would never see the
   * chip that was pressed.
   *
   * @param {Event} event
   */
  handleChip(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const chip = target.closest('[data-lift-chip]');
    if (!chip || !this.contains(chip)) return;

    this.#tag = /** @type {HTMLElement} */ (chip).dataset.liftChip ?? '';
    // A new filter starts closed again, or the button would already say
    // "show less" over a set the visitor has not opened.
    this.#expanded = false;
    this.#render();
  }

  toggleMore() {
    this.#expanded = !this.#expanded;
    this.#render();
  }

  /**
   * A card the editor just selected must be on screen, whatever the filter and
   * the limit say — otherwise clicking a review in the sidebar shows nothing.
   *
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Node) || !this.contains(target)) return;

    this.#tag = '';
    this.#expanded = true;
    this.#render();
  };

  #render() {
    const cards = this.#cards;
    const matches = this.#tag
      ? cards.filter(
          (card) => card.dataset.size === this.#tag || card.dataset.occasion === this.#tag
        )
      : cards;

    // No hit is not an empty wall: the box says so and the first few reviews
    // stand in, the way the prototype does it.
    const nothingFound = matches.length === 0;
    const shown = nothingFound ? cards.slice(0, 4) : matches;
    const limit = this.#expanded ? shown.length : this.#limit;

    for (const card of cards) {
      const position = shown.indexOf(card);
      card.hidden = position === -1 || position >= limit;
    }

    if (this.refs.empty) this.refs.empty.hidden = !nothingFound;

    if (this.refs.count) {
      const word = this.dataset.liftCountWord ?? '';
      this.refs.count.textContent = `${matches.length} ${word}`.trim();
    }

    this.#syncChips();
    this.#syncButton(shown.length);
  }

  #syncChips() {
    for (const chip of this.#chips) {
      chip.setAttribute('aria-pressed', String((chip.dataset.liftChip ?? '') === this.#tag));
    }
  }

  /** @param {number} available - Cards the current filter leaves. */
  #syncButton(available) {
    const button = this.refs.more;
    if (!button) return;

    // Nothing to open, nothing to offer.
    button.hidden = available <= this.#limit;
    button.setAttribute('aria-expanded', String(this.#expanded));

    const label = this.#expanded ? button.dataset.liftLabelLess : button.dataset.liftLabelMore;
    if (label) button.textContent = label;
  }
}

if (!customElements.get('lift-reviews-component')) {
  customElements.define('lift-reviews-component', LiftReviews);
}
